import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { reservationAPI } from '../api/axios';
import Layout from '../components/Layout';
import Button from '../components/Button';
import './TokenCancelPage.css';

/**
 * 토큰 기반 예약 취소 페이지
 * - 카카오 알림톡의 '예약 취소' 버튼 클릭 시 이동하는 페이지
 * - URL: /cancel?token={cancelToken}
 * - 인증 불필요 (토큰으로 예약 식별)
 */
const TokenCancelPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  // 상태: idle(초기) | confirming(취소 확인 중) | loading(API 호출 중) | success | error
  const [status, setStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');

  // 토큰 유효성 기본 체크
  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage('유효하지 않은 취소 링크입니다. 알림톡에서 다시 시도해주세요.');
    }
  }, [token]);

  // 취소 확인 단계로 이동
  const handleConfirmStep = () => {
    setStatus('confirming');
  };

  // 취소 API 호출
  const handleCancel = async () => {
    setStatus('loading');
    setErrorMessage('');

    try {
      await reservationAPI.cancelByToken(token);
      setStatus('success');
    } catch (err) {
      setStatus('error');
      const message = err.response?.data?.message || '예약 취소 중 오류가 발생했습니다';
      setErrorMessage(message);
    }
  };

  // 돌아가기 (취소 확인 → 초기 상태)
  const handleBack = () => {
    setStatus('idle');
  };

  // === 렌더링 ===

  // 토큰 없음 또는 에러
  if (status === 'error') {
    return (
      <Layout>
        <div className="token-cancel-page">
          <div className="token-cancel-card">
            <div className="token-cancel-icon error-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
            </div>
            <h2 className="token-cancel-title">취소할 수 없습니다</h2>
            <p className="token-cancel-description">{errorMessage}</p>
            <div className="token-cancel-actions">
              <a href="/" className="back-to-home-link">메인으로 돌아가기</a>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // 취소 성공
  if (status === 'success') {
    return (
      <Layout>
        <div className="token-cancel-page">
          <div className="token-cancel-card">
            <div className="token-cancel-icon success-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <polyline points="16 10 11 15 8 12" />
              </svg>
            </div>
            <h2 className="token-cancel-title">예약이 취소되었습니다</h2>
            <p className="token-cancel-description">
              예약이 성공적으로 취소되었습니다.<br />
              재예약을 원하시면 언제든 연락주세요.
            </p>
            <div className="token-cancel-actions">
              <Button
                variant="primary"
                size="large"
                fullWidth
                onClick={() => window.location.href = '/reservation'}
              >
                새 예약하기
              </Button>
              <a href="/" className="back-to-home-link">메인으로 돌아가기</a>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // 취소 확인 단계
  if (status === 'confirming' || status === 'loading') {
    return (
      <Layout>
        <div className="token-cancel-page">
          <div className="token-cancel-card">
            <div className="token-cancel-icon warning-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <h2 className="token-cancel-title">정말 취소하시겠습니까?</h2>
            <p className="token-cancel-description">
              취소된 예약은 복구할 수 없습니다.<br />
              재예약이 필요하시면 다시 예약해주세요.
            </p>
            <div className="token-cancel-actions">
              <Button
                variant="danger"
                size="large"
                fullWidth
                onClick={handleCancel}
                loading={status === 'loading'}
                disabled={status === 'loading'}
              >
                {status === 'loading' ? '취소 처리 중...' : '예약 취소하기'}
              </Button>
              <Button
                variant="outline"
                size="large"
                fullWidth
                onClick={handleBack}
                disabled={status === 'loading'}
              >
                돌아가기
              </Button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // 초기 상태 (idle)
  return (
    <Layout>
      <div className="token-cancel-page">
        <div className="token-cancel-card">
          <div className="token-cancel-icon cancel-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
              <line x1="14" y1="14" x2="10" y2="18" />
              <line x1="10" y1="14" x2="14" y2="18" />
            </svg>
          </div>
          <h2 className="token-cancel-title">예약 취소</h2>
          <p className="token-cancel-description">
            예약을 취소하시려면 아래 버튼을 눌러주세요.<br />
            예약 시간 1시간 전까지 취소 가능합니다.
          </p>
          <div className="token-cancel-actions">
            <Button
              variant="danger"
              size="large"
              fullWidth
              onClick={handleConfirmStep}
            >
              예약 취소 진행
            </Button>
            <a href="/" className="back-to-home-link">메인으로 돌아가기</a>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default TokenCancelPage;
